import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

interface SupportTicket {
  _id: string;
  userId: { _id: string; name: string; email: string; phone?: string } | null;
  category: string;
  subject: string;
  message: string;
  status: 'open' | 'in_review' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high';
  adminResponse?: string | null;
  respondedAt?: string | null;
  respondedBy?: { name: string; email: string } | null;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  open: 'error',
  in_review: 'warning',
  resolved: 'success',
  closed: 'default',
};

const PRIORITY_COLORS: Record<string, 'default' | 'warning' | 'error'> = {
  low: 'default',
  normal: 'default',
  high: 'error',
};

const fetchTickets = async (params: Record<string, any>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) query.append(k, String(v)); });
  const res = await api.get(`/admin/support-tickets?${query.toString()}`);
  return res.data as { tickets: SupportTicket[]; pagination: { page: number; total: number; pages: number } };
};

const SupportTickets: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-support-tickets', page, statusFilter, priorityFilter, categoryFilter],
    queryFn: () => fetchTickets({ page, status: statusFilter, priority: priorityFilter, category: categoryFilter }),
    keepPreviousData: true,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.patch(`/admin/support-tickets/${id}`, payload).then((r) => r.data.ticket as SupportTicket),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['admin-support-tickets'] });
      setSelected(updated);
      toast.success('Ticket updated');
    },
    onError: () => toast.error('Failed to update ticket'),
  });

  const handleOpen = (ticket: SupportTicket) => {
    setSelected(ticket);
    setReplyText(ticket.adminResponse || '');
    setNewStatus(ticket.status);
  };

  const handleSave = () => {
    if (!selected) return;
    const payload: Record<string, any> = { status: newStatus };
    if (replyText.trim()) payload.adminResponse = replyText.trim();
    updateMutation.mutate({ id: selected._id, payload });
  };

  const tickets = data?.tickets || [];
  const pagination = data?.pagination;

  if (isLoading && !data) return <LoadingSpinner message="Loading support tickets..." />;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" mb={3}>
        Support Tickets
        {pagination && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
            ({pagination.total} total)
          </Typography>
        )}
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_review">In Review</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select value={priorityFilter} label="Priority" onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="invalid_contact">Invalid Contact</MenuItem>
                  <MenuItem value="payment">Payment</MenuItem>
                  <MenuItem value="ride_quality">Ride Quality</MenuItem>
                  <MenuItem value="account">Account</MenuItem>
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {tickets.length === 0 ? (
        <Alert severity="info">No support tickets found.</Alert>
      ) : (
        <>
          <Stack spacing={2} mb={3}>
            {tickets.map((ticket) => (
              <Card key={ticket._id} variant="outlined" sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }} onClick={() => handleOpen(ticket)}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>{ticket.subject}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>{ticket.message}</Typography>
                      <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                        {ticket.userId?.name || 'Unknown'} · {ticket.userId?.email || '—'} · {new Date(ticket.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexShrink={0} alignItems="center">
                      <Chip label={ticket.category.replace('_', ' ')} size="small" variant="outlined" />
                      <Chip label={ticket.priority} size="small" color={PRIORITY_COLORS[ticket.priority]} />
                      <Chip label={ticket.status.replace('_', ' ')} size="small" color={STATUS_COLORS[ticket.status]} />
                      {ticket.adminResponse && <Chip label="Replied" size="small" color="success" variant="outlined" />}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {pagination && pagination.pages > 1 && (
            <Box display="flex" justifyContent="center">
              <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Ticket detail / respond dialog */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box>
                <Typography variant="h6" fontWeight={700}>{selected.subject}</Typography>
                <Stack direction="row" spacing={1} mt={0.5}>
                  <Chip label={selected.category.replace('_', ' ')} size="small" variant="outlined" />
                  <Chip label={selected.priority} size="small" color={PRIORITY_COLORS[selected.priority]} />
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>FROM</Typography>
                  <Typography variant="body2">{selected.userId?.name || 'Unknown'} · {selected.userId?.email}</Typography>
                  {selected.userId?.phone && <Typography variant="body2" color="text.secondary">{selected.userId.phone}</Typography>}
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>MESSAGE</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{selected.message}</Typography>
                  <Typography variant="caption" color="text.secondary">{new Date(selected.createdAt).toLocaleString()}</Typography>
                </Box>

                {selected.adminResponse && (
                  <Box sx={{ p: 1.5, bgcolor: '#f0f7ff', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>PREVIOUS REPLY</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{selected.adminResponse}</Typography>
                    {selected.respondedAt && (
                      <Typography variant="caption" color="text.secondary">{new Date(selected.respondedAt).toLocaleString()}</Typography>
                    )}
                  </Box>
                )}

                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={newStatus} label="Status" onChange={(e) => setNewStatus(e.target.value)}>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_review">In Review</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Reply to driver"
                  multiline
                  rows={4}
                  fullWidth
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a response that will be visible to the driver..."
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={updateMutation.isLoading}>
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
